/* eslint-disable max-len */
import React, { FC } from 'react';
import { Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { SubmitButton, Form, Input } from 'formik-antd/lib';
// import { Divider, Button } from 'antd';
import getValidationSchema, {
  INITIAL_VALUES,
  Values,
} from './LoginForm.schema';
import './LoginForm.css';
import classes from './LoginForm.module.less';
import FormItem from '../../../../../components/FormItem';
import AuthErrorMessage from '../../../../../components/AuthErrorMessage';
import { getAuthError } from '../../../slice/selectors';
import { polishServerText } from '../../../../../utils/apiErrors';

type LoginFormProps = {
  onSubmit: (values: Values) => void;
};

const LoginForm: FC<LoginFormProps> = ({ onSubmit }) => {
  const { t } = useTranslation();
  const isAuthenticating = false;
  const validationSchema = getValidationSchema(t);
  // const goToSignUp = useGoToRoute(Path.SIGN_UP);
  const authError = useSelector(getAuthError);
  // 401 i 400 to źle wpisane dane (400: adres bez postaci e-maila albo za krótkie hasło), reszta to awaria.
  // Uczeń, który nie aktywował konta, też dostaje 401 — stąd podpowiedź o linku.
  const isWrongCredentials = authError?.statusCode === 401 || authError?.statusCode === 400;
  // Blokada po zbyt wielu próbach: polski tekst backendu z liczbą minut. Front limitów nie zna.
  // Zablokowany jest najczęściej ktoś, kto zapomniał hasła — stąd ta sama podpowiedź co przy złym haśle:
  // nowe hasło z linku od nauczyciela od razu zdejmuje blokadę
  // Przy innym języku niż polski zamiast tekstu backendu ogólny tekst o blokadzie
  const isTooManyAttempts = authError?.code === 'LOGIN_TOO_MANY_ATTEMPTS' && typeof authError.message === 'string';
  let errorText = t(isWrongCredentials ? 'common:login.errors.wrongCredentials' : 'common:login.errors.generic');

  if (isTooManyAttempts) {
    errorText = polishServerText(authError.message) || t('common:login.errors.tooManyAttempts');
  }

  return (
    <Formik
      initialValues={INITIAL_VALUES}
      onSubmit={onSubmit}
      validationSchema={validationSchema}
    >
      {({ errors, touched, values }) => (
        <Form className={classes.form} layout="vertical" data-cy="loginForm" id="login-form">
          <FormItem
            name="email"
            labelKey="common:login.labels.email"
            className={classes.emailField}
            error={errors.email}
            isTouched={touched.email}
            value={values.email}
          />
          <FormItem
            name="password"
            labelKey="common:login.labels.password"
            className={classes.passwordField}
            error={errors.password}
            Component={Input.Password}
            isTouched={touched.password}
            value={values.password}
          />
          {/* Link „Zapomniałeś hasła ?” schowany, dopóki backend nie ma tej funkcji — zob. docs/TODO.md.
              Odstęp, który dawał jego wiersz (.row), ma teraz przycisk. */}
          <SubmitButton
            disabled={false}
            loading={isAuthenticating}
            data-cy="submitButton"
            size="large"
            block
            style={{ borderRadius: '6px', marginTop: 'clamp(16px, 5vmin, 40px)' }}
          >
            {t('common:login.labels.submit')}
          </SubmitButton>
          {/* Błąd pod przyciskiem, nie nad formularzem — tuż przy miejscu, które się kliknęło */}
          {authError && (
            <AuthErrorMessage
              hint={(isWrongCredentials || isTooManyAttempts) && (
                <>
                  {t('common:login.errors.noPassword')}
                  <br />
                  {t('common:login.errors.teacherContact')}
                </>
              )}
            >
              {errorText}
            </AuthErrorMessage>
          )}
          {/* <Divider plain style={{ color: 'lightgrey' }}>{t('common:or')}</Divider> */}
          {/* <Button
            disabled={false}
            loading={isAuthenticating}
            data-cy="signUpButton"
            size="large"
            block
            onClick={goToSignUp}
            style={{ borderRadius: '6px' }}
          >
            {t('common:login.labels.signUp')}
          </Button> */}
        </Form>
      )}
    </Formik>
  );
};

export default LoginForm;
